import {
  Box,
  Button,
  Flex,
  FormControl,
  FormHelperText,
  FormLabel,
  IconButton,
  Select,
  Spinner,
  Stack,
  Tag,
  Text,
  Tooltip,
} from '@chakra-ui/react';
import { useContext, useMemo, useState } from 'react';
import { FiX } from 'react-icons/fi';
import { MultiSelect } from 'react-multi-select-component';

import { SetToggleThemeContext } from '../../../contexts/SetToggleThemeContext';
import {
  AccessControlItem,
  AccessControlKind,
  AccessControlUser,
  accessControlService,
} from '../../../services/accessControlService';
import {
  diffIds,
  filterOptions,
  plural,
  toUserOptions,
} from '../../../utils/accessControl';
import { Internationalization } from '../../../utils/InternationalizationMultSelect';
import { apllyToast } from '../../Global/Toast2.0';
import { Container as MultiSelectContainer } from '../Modal/ModalAddAction/styles';

export interface CopySource {
  kind: AccessControlKind;
  item: Pick<AccessControlItem, 'id' | 'name' | 'total_accesses'>;
}

interface Props {
  users: AccessControlUser[];
  isLoading?: boolean;
  initialIds: string[];
  value: string[];
  onChange: (ids: string[]) => void;
  copySources?: CopySource[];
}

// Escolhe os usuários de uma permissão ou função. A busca não diferencia
// acento e procura no nome, no login e no setor ("contabilidade" acha o setor
// inteiro, e "Selecionar todos os encontrados" marca todos de uma vez).
export function ItemUsersEditor({
  users,
  isLoading = false,
  initialIds,
  value,
  onChange,
  copySources = [],
}: Props) {
  const { toggleTheme } = useContext(SetToggleThemeContext);
  const [copying, setCopying] = useState(false);
  const [copyKey, setCopyKey] = useState('');

  const options = useMemo(() => toUserOptions(users, true), [users]);
  const byId = useMemo(() => {
    const map: Record<string, AccessControlUser | undefined> = {};

    users.forEach(user => {
      map[user.id] = user;
    });

    return map;
  }, [users]);

  const selectedOptions = options.filter(option =>
    value.includes(option.value),
  );
  const { added, removed } = diffIds(initialIds, value);
  const selectedUsers = value
    .map(id => byId[id])
    .filter((user): user is AccessControlUser => !!user)
    .sort((a, b) => a.name.localeCompare(b.name, 'pt-BR'));
  const inactive = selectedUsers.filter(user => !user.status).length;

  const handleCopy = async (key: string) => {
    setCopyKey(key);

    const source = copySources.find(
      item => `${item.kind}:${item.item.id}` === key,
    );

    if (!source) {
      return;
    }

    setCopying(true);
    try {
      const sourceUsers = await accessControlService.listItemUsers(
        source.kind,
        source.item.id,
      );
      const merged = Array.from(
        new Set([...value, ...sourceUsers.map(user => user.id)]),
      );

      onChange(merged);
      apllyToast(
        'success',
        `${plural(
          merged.length - value.length,
          'usuário adicionado',
          'usuários adicionados',
        )} de "${source.item.name}".`,
      );
    } catch {
      apllyToast('error', 'Não foi possível copiar os usuários.');
    } finally {
      setCopying(false);
      setCopyKey('');
    }
  };

  const remove = (id: string) => onChange(value.filter(item => item !== id));

  const handleSelect = (selected: { value: string }[]) => {
    onChange(selected.map(option => option.value));
  };

  const undoRemovals = () => {
    onChange(Array.from(new Set([...value, ...removed])));
  };

  return (
    <Stack spacing="4">
      <FormControl>
        <FormLabel>Usuários</FormLabel>
        {isLoading ? (
          <Spinner size="sm" />
        ) : (
          <MultiSelectContainer theme={toggleTheme}>
            <MultiSelect
              options={options}
              value={selectedOptions}
              onChange={handleSelect}
              filterOptions={filterOptions}
              labelledBy="Usuários"
              overrideStrings={{
                ...Internationalization,
                selectSomeItems: 'Buscar por nome, login ou setor...',
                allItemsAreSelected: 'Todos os usuários selecionados.',
              }}
            />
          </MultiSelectContainer>
        )}
        <FormHelperText>
          Digite o setor (ex.: contabilidade) e use &quot;Selecionar todos os
          encontrados&quot; para marcar o setor inteiro.
        </FormHelperText>
      </FormControl>

      {copySources.length > 0 && (
        <FormControl>
          <FormLabel htmlFor="copy-users">Copiar usuários de</FormLabel>
          <Flex align="center" gridGap="2">
            <Select
              id="copy-users"
              value={copyKey}
              placeholder="Escolha uma permissão ou função"
              onChange={event => handleCopy(event.target.value)}
              isDisabled={copying || isLoading}
            >
              {(['permission', 'role'] as const).map(kind => {
                const items = copySources.filter(item => item.kind === kind);

                return items.length > 0 ? (
                  <optgroup
                    key={kind}
                    label={kind === 'permission' ? 'Permissões' : 'Funções'}
                  >
                    {items.map(item => (
                      <option
                        key={item.item.id}
                        value={`${kind}:${item.item.id}`}
                      >
                        {item.item.name} ({item.item.total_accesses})
                      </option>
                    ))}
                  </optgroup>
                ) : null;
              })}
            </Select>
            {copying && <Spinner size="sm" />}
          </Flex>
          <FormHelperText>
            Soma os usuários da outra permissão ou função aos escolhidos aqui.
          </FormHelperText>
        </FormControl>
      )}

      <Box>
        <Flex justify="space-between" align="center" wrap="wrap" gridGap="2">
          <Text fontWeight="medium">
            {plural(value.length, 'usuário escolhido', 'usuários escolhidos')}
          </Text>
          <Flex gridGap="2" wrap="wrap">
            {added.length > 0 && (
              <Tag size="sm" colorScheme="green">
                +{added.length} {added.length === 1 ? 'novo' : 'novos'}
              </Tag>
            )}
            {removed.length > 0 && (
              <Tag size="sm" colorScheme="red">
                −{removed.length}{' '}
                {removed.length === 1 ? 'removido' : 'removidos'}
              </Tag>
            )}
            {inactive > 0 && (
              <Tooltip
                bg="gray.650"
                label="Usuários inativos não entram no sistema, mas mantêm o vínculo."
              >
                <Tag size="sm" colorScheme="gray">
                  {plural(inactive, 'inativo', 'inativos')}
                </Tag>
              </Tooltip>
            )}
          </Flex>
        </Flex>

        {selectedUsers.length > 0 && (
          <Box
            mt="2"
            maxH="260px"
            overflowY="auto"
            borderWidth="1px"
            borderColor="gray.600"
            borderRadius="6px"
          >
            {selectedUsers.map(user => (
              <Flex
                key={user.id}
                align="center"
                justify="space-between"
                gridGap="2"
                px="3"
                py="1.5"
                borderBottomWidth="1px"
                borderColor="gray.700"
              >
                <Box minW="0">
                  <Text fontSize="sm" noOfLines={1}>
                    {user.name}
                  </Text>
                  <Text fontSize="xs" color="gray.500" noOfLines={1}>
                    {[user.nickname, user.sector].filter(Boolean).join(' · ')}
                  </Text>
                </Box>
                <Flex align="center" gridGap="2" flexShrink={0}>
                  {!initialIds.includes(user.id) && (
                    <Tag size="sm" colorScheme="green">
                      novo
                    </Tag>
                  )}
                  {!user.status && (
                    <Tag size="sm" colorScheme="gray">
                      Inativo
                    </Tag>
                  )}
                  <IconButton
                    aria-label={`Remover ${user.name}`}
                    icon={<FiX />}
                    size="xs"
                    variant="ghost"
                    onClick={() => remove(user.id)}
                  />
                </Flex>
              </Flex>
            ))}
          </Box>
        )}

        {removed.length > 0 && (
          <Button size="xs" variant="link" mt="2" onClick={undoRemovals}>
            Desfazer remoções
          </Button>
        )}
      </Box>
    </Stack>
  );
}
